import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Chip, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Button, IconButton,
  Skeleton, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment, Snackbar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ArrowBack, Payment, School } from '@mui/icons-material';
import dayjs from 'dayjs';
import { api } from '../api';

const PAYMENT_METHODS = ['Cash', 'Venmo', 'Zelle', 'Check', 'Bank Transfer', 'Other'];

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: dayjs(), method: '', notes: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchReport = async () => {
    try {
      const data = await api.getStudentReport(id);
      setReport(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [id]);

  const openPayDialog = () => {
    setPayForm({
      amount: report?.balance_owed > 0 ? report.balance_owed.toFixed(2) : '',
      payment_date: dayjs(),
      method: '',
      notes: ''
    });
    setPayDialogOpen(true);
  };

  const handlePaySave = async () => {
    try {
      await api.createPayment({
        student_id: id,
        amount: parseFloat(payForm.amount),
        payment_date: payForm.payment_date.format('YYYY-MM-DD'),
        method: payForm.method,
        notes: payForm.notes
      });
      setPayDialogOpen(false);
      setSnackbar({ open: true, message: 'Payment recorded!', severity: 'success' });
      fetchReport();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  if (loading) return <Box sx={{ p: 2 }}><Skeleton height={200} /><Skeleton height={300} /></Box>;
  if (!report) return <Box sx={{ p: 2 }}><Alert severity="error">Student not found</Alert></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/students')}><ArrowBack /></IconButton>
        <Typography variant="h5" fontWeight={700}>{report.student.name}</Typography>
      </Box>

      {/* Balance Summary */}
      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' }}>
        <CardContent>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>Balance Overview</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>Total Charges</Typography>
              <Typography variant="h6">${report.total_charges.toFixed(2)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>Total Paid</Typography>
              <Typography variant="h6">${report.total_paid.toFixed(2)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {report.balance_owed >= 0 ? 'Owed' : 'Credit'}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                ${Math.abs(report.balance_owed).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button variant="contained" startIcon={<Payment />} onClick={openPayDialog} fullWidth>
          Record Payment
        </Button>
      </Box>

      {/* Unpaid Dates */}
      {report.unpaid_dates.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>Unpaid Sessions</Typography>
          <Card sx={{ mb: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.unpaid_dates.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.date}</TableCell>
                      <TableCell>{item.class_name}</TableCell>
                      <TableCell align="right">
                        ${item.charge.toFixed(2)}
                        {item.partially_paid > 0 && (
                          <Chip size="small" label={`$${item.partially_paid.toFixed(2)} paid`} color="warning" sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* Attendance History */}
      <Typography variant="h6" sx={{ mb: 1 }}>Attendance History</Typography>
      <Card sx={{ mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Class</TableCell>
                <TableCell align="right">Charge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.attendance_records.length === 0 ? (
                <TableRow><TableCell colSpan={3} align="center">No attendance records</TableCell></TableRow>
              ) : (
                report.attendance_records.map((record, i) => (
                  <TableRow key={i}>
                    <TableCell>{record.session_date}</TableCell>
                    <TableCell>{record.class_name}</TableCell>
                    <TableCell align="right">${record.charge_amount.toFixed(2)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Payment History */}
      <Typography variant="h6" sx={{ mb: 1 }}>Payment History</Typography>
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.payments.length === 0 ? (
                <TableRow><TableCell colSpan={3} align="center">No payments recorded</TableCell></TableRow>
              ) : (
                report.payments.map((payment, i) => (
                  <TableRow key={i}>
                    <TableCell>{payment.payment_date}</TableCell>
                    <TableCell>{payment.method || '-'}</TableCell>
                    <TableCell align="right">${payment.amount.toFixed(2)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={payDialogOpen} onClose={() => setPayDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record Payment — {report.student.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Amount" value={payForm.amount}
            onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
            type="number" sx={{ mt: 1, mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            autoFocus
          />
          <DatePicker
            label="Payment Date"
            value={payForm.payment_date}
            onChange={(val) => setPayForm({ ...payForm, payment_date: val })}
            sx={{ width: '100%', mb: 2 }}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={payForm.method}
              onChange={e => setPayForm({ ...payForm, method: e.target.value })}
              label="Payment Method"
            >
              {PAYMENT_METHODS.map(m => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth label="Notes (optional)" value={payForm.notes}
            onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
            multiline rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPayDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePaySave} disabled={!payForm.amount}>
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
